import { Clock3, Eye, ImageOff, ImageUp, Pencil, Skull, ThumbsUp, Timer, Wand2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'

import {
  useRetentionAudit,
  type RetentionAuditItem,
  type RetentionAuditSummary,
  type RetentionVerdict,
} from '@/api/endpoints/analytics'
import { EmptyState } from '@/components/ds/EmptyState'
import { Section } from '@/components/ds/Section'
import { Button } from '@/components/ui/Button'
import { Pagination } from '@/components/ui/Pagination'
import { Tooltip } from '@/components/ui/Tooltip'
import { SortableHead } from '@/features/analytics/shared/SortableHead'
import { nextSort, type SortState } from '@/features/analytics/shared/sort'
import { cn } from '@/lib/cn'
import { preservingChannel } from '@/lib/routing'

import { PresentationEditDialog } from './PresentationEditDialog'
import styles from './RetentionAuditSection.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

// Pull the entire dataset in one shot so filter + sort can run client-side.
// Channels rarely exceed a few hundred uploads (200 is the server's MAX_LIMIT),
// and the verdict pills must always reflect the full population — paginating
// server-side would either show partial counts or break filter accuracy.
const FETCH_LIMIT = 200

type SortCol = 'verdict' | 'age' | 'retention' | 'views' | 'delivery'

export interface RetentionAuditSectionProps {
  channel: string
  /** 1-based current page (URL-controlled by the parent). */
  page: number
  /** Items per page (URL-controlled, default 25 — matches Prism's table convention). */
  pageSize: number
  onPageChange: (next: number) => void
  onPageSizeChange: (next: number) => void
}

/**
 * "Análise de retenção" — deterministic per-video verdicts on whether each
 * upload deserves a thumb/title edit, should be left alone, killed or has
 * no signal yet (``wait``). The verdict is computed server-side from
 * ``video_performance_snapshots`` (retention) cross-referenced with
 * ``youtube_video_stats`` (delivery) and ``youtube_uploads.uploaded_at``
 * (age) — it is NOT an LLM call. Strategic commentary on top of these
 * verdicts is a separate concern and lives behind its own button.
 *
 * The verdict pills double as filters: clicking one narrows the table to
 * that category. Column headers double as sort buttons (asc/desc/none).
 * Both controls are local UI state — the underlying ``useRetentionAudit``
 * still fetches the full set so counts stay accurate when you toggle a
 * filter on or off.
 */
export function RetentionAuditSection({
  channel,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: RetentionAuditSectionProps) {
  const query = useRetentionAudit({ channel, limit: FETCH_LIMIT, offset: 0 })

  // Dialog target lives at the section level — there's at most one open
  // at a time (modal), and rows would lose state on re-render if each
  // owned its own.
  const [editTarget, setEditTarget] = useState<{
    videoId: string
    language: string
    currentTitle: string
  } | null>(null)
  // null = no filter, otherwise restricts the table to that verdict.
  const [verdictFilter, setVerdictFilter] = useState<RetentionVerdict | null>(null)
  // null = no explicit sort (preserves backend order, which is uploaded_at desc).
  const [sort, setSort] = useState<SortState<SortCol> | null>(null)

  const summary = query.data?.summary ?? null
  // Filter+sort live inside the memo so the dep array references the stable
  // ``query.data`` object instead of a fresh ``[]`` we'd create above on
  // every render — was the lint warning the dev compiler flagged.
  const visibleItems = useMemo(() => {
    const all = query.data?.items ?? []
    const filtered = verdictFilter ? all.filter((r) => r.verdict === verdictFilter) : all
    if (!sort) return filtered
    const sorted = filtered.slice().sort(comparators[sort.col])
    return sort.dir === 'desc' ? sorted.reverse() : sorted
  }, [query.data, verdictFilter, sort])

  const totalVisible = visibleItems.length
  const pageItems = visibleItems.slice((page - 1) * pageSize, page * pageSize)

  const subtitle = summary
    ? `${String(summary.total)} vídeo${summary.total === 1 ? '' : 's'} · mediana ${formatCount(Math.round(summary.channel_median_views))} views${
        summary.channel_median_avp != null
          ? ` · retenção mediana ${(summary.channel_median_avp * 100).toFixed(0)}%`
          : ''
      }`
    : 'Carregando...'

  function toggleVerdictFilter(v: RetentionVerdict): void {
    setVerdictFilter((prev) => (prev === v ? null : v))
    onPageChange(1)
  }

  function toggleSort(col: SortCol): void {
    setSort((prev) => nextSort(prev, col))
  }

  return (
    <Section title="Análise de retenção" description={subtitle}>
      <div className={styles.shell}>
        <p className={styles.intro}>
          Cada vídeo é cruzado em três eixos — idade, retenção (AVP) e entrega vs mediana do canal — pra
          decidir se vale mexer. Determinístico, sem LLM. <strong>Editar thumb/título</strong> só é sugerido
          quando o vídeo pode realmente reagir.
        </p>

        {query.isError ? (
          <QueryErrorNotice
            error={query.error}
            label="a análise"
            onRetry={() => {
              void query.refetch()
            }}
          />
        ) : query.isPending ? (
          <TableSkeleton />
        ) : (summary?.total ?? 0) === 0 ? (
          <EmptyState
            icon={<Eye />}
            title="Sem uploads publicados ainda"
            description="Quando o canal tiver vídeos publicados, eles aparecem aqui com veredito por vídeo."
          />
        ) : (
          <>
            {summary ? (
              <SummaryStrip
                summary={summary}
                activeVerdict={verdictFilter}
                onToggleVerdict={toggleVerdictFilter}
              />
            ) : null}
            {totalVisible === 0 ? (
              <EmptyState
                icon={<Eye />}
                title="Nenhum vídeo nesse filtro"
                description="Clique no veredito ativo de novo pra remover o filtro."
              />
            ) : (
              <>
                <Table
                  rows={pageItems}
                  onRequestEdit={setEditTarget}
                  sort={sort}
                  onToggleSort={toggleSort}
                />
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  total={totalVisible}
                  onPageChange={onPageChange}
                  onPageSizeChange={onPageSizeChange}
                  itemLabel="vídeo"
                  itemLabelPlural="vídeos"
                />
              </>
            )}
          </>
        )}
      </div>
      <PresentationEditDialog target={editTarget} onClose={() => { setEditTarget(null) }} />
    </Section>
  )
}

// ---------------------------------------------------------------------------
// Summary strip — verdict counts double as filter pills. Clicking the active
// pill clears the filter; clicking a different one switches.
// ---------------------------------------------------------------------------

interface SummaryStripProps {
  summary: RetentionAuditSummary
  activeVerdict: RetentionVerdict | null
  onToggleVerdict: (v: RetentionVerdict) => void
}

function SummaryStrip({ summary, activeVerdict, onToggleVerdict }: SummaryStripProps) {
  const v = summary.by_verdict
  return (
    <div className={styles.summaryStrip}>
      <SummaryPill verdict="keep" count={v.keep ?? 0} active={activeVerdict === 'keep'} onClick={onToggleVerdict} />
      <SummaryPill verdict="edit_thumb_title" count={v.edit_thumb_title ?? 0} active={activeVerdict === 'edit_thumb_title'} onClick={onToggleVerdict} />
      <SummaryPill verdict="wait" count={v.wait ?? 0} active={activeVerdict === 'wait'} onClick={onToggleVerdict} />
      <SummaryPill verdict="kill" count={v.kill ?? 0} active={activeVerdict === 'kill'} onClick={onToggleVerdict} />
    </div>
  )
}

interface SummaryPillProps {
  verdict: RetentionVerdict
  count: number
  active: boolean
  onClick: (v: RetentionVerdict) => void
}

function SummaryPill({ verdict, count, active, onClick }: SummaryPillProps) {
  const meta = VERDICT_META[verdict]
  const disabled = count === 0 && !active
  return (
    <Tooltip content={<span style={{ maxWidth: 280, display: 'inline-block' }}>{VERDICT_TOOLTIP[verdict]}</span>}>
      <button
        type="button"
        className={cn(
          styles.summaryPill,
          styles[`pill_${verdict}`],
          active && styles.pillActive,
          disabled && styles.pillDisabled,
        )}
        onClick={() => { if (!disabled) onClick(verdict) }}
        disabled={disabled}
        aria-pressed={active}
        aria-label={`Filtrar por ${meta.label.toLowerCase()}: ${String(count)} vídeo${count === 1 ? '' : 's'}`}
      >
        <span className={styles.pillIcon} aria-hidden>
          {meta.icon}
        </span>
        <span className={styles.pillCount}>{String(count)}</span>
        <span className={styles.pillLabel}>{meta.label}</span>
      </button>
    </Tooltip>
  )
}

// ---------------------------------------------------------------------------
// Table — column headers double as sort triggers. The verdict / title
// columns aren't sortable yet (verdict already filters via the strip; title
// would force operators to scroll a wall of titles instead of acting on
// data signals).
// ---------------------------------------------------------------------------

interface TableProps {
  rows: RetentionAuditItem[]
  onRequestEdit: (target: { videoId: string; language: string; currentTitle: string }) => void
  sort: SortState<SortCol> | null
  onToggleSort: (col: SortCol) => void
}

function Table({ rows, onRequestEdit, sort, onToggleSort }: TableProps) {
  return (
    <div className={styles.tableWrap}>
      <div className={cn(styles.row, styles.head)}>
        <SortableHead col="verdict" label="Veredito" sort={sort} onClick={onToggleSort} />
        <span>Vídeo</span>
        <SortableHead col="age" label="Idade" sort={sort} onClick={onToggleSort} align="right" />
        <SortableHead col="retention" label="Retenção" sort={sort} onClick={onToggleSort} align="right" />
        <SortableHead col="views" label="Views" sort={sort} onClick={onToggleSort} align="right" />
        <SortableHead col="delivery" label="Entrega" sort={sort} onClick={onToggleSort} align="right" />
        <span style={{ textAlign: 'right' }}>Ação</span>
      </div>
      <div className={styles.list}>
        {rows.map((r) => (
          <Row key={`${r.video_id}-${r.language}`} row={r} onRequestEdit={onRequestEdit} />
        ))}
      </div>
    </div>
  )
}

interface RowProps {
  row: RetentionAuditItem
  onRequestEdit: (target: { videoId: string; language: string; currentTitle: string }) => void
}

function Row({ row, onRequestEdit }: RowProps) {
  const meta = VERDICT_META[row.verdict]
  const avpDisplay = row.avp != null ? `${String(Math.round(row.avp * 100))}%` : '—'
  const deliveryLabel = row.delivery_bucket === 'over' ? 'acima' : 'abaixo'
  return (
    <div className={styles.row}>
      <span className={cn(styles.verdictBadge, styles[`badge_${row.verdict}`])} title={row.reason}>
        <span className={styles.badgeIcon} aria-hidden>
          {meta.icon}
        </span>
        {meta.label}
      </span>
      <span className={styles.titleCell}>
        <StudioThumbMark status={row.studio_thumbnail_status ?? null} error={row.studio_thumbnail_error} />
        <Link
          to="/projects/$projectId"
          params={{ projectId: row.video_id }}
          search={preservingChannel({ lang: row.language })}
          className={styles.title}
          title={row.title || row.video_id}
        >
          {row.title || `(sem título — ${row.video_id})`}
        </Link>
      </span>
      <span className={styles.cellRight}>
        {row.age_days}d
        <small className={styles.cellHint}> · {AGE_LABEL[row.age_bucket]}</small>
      </span>
      <span className={styles.cellRight}>
        {avpDisplay}
        {row.retention_bucket !== 'unknown' ? (
          <small className={styles.cellHint}> · {RET_LABEL[row.retention_bucket]}</small>
        ) : null}
      </span>
      <span className={styles.cellRight}>{formatCount(row.views)}</span>
      <span
        className={cn(
          styles.cellRight,
          row.delivery_bucket === 'over' ? styles.deliveryOver : styles.deliveryUnder,
        )}
      >
        {deliveryLabel}
      </span>
      <span className={styles.actionCell}>
        {row.verdict === 'edit_thumb_title' ? (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Wand2 />}
            onClick={() => {
              onRequestEdit({
                videoId: row.video_id,
                language: row.language,
                currentTitle: row.title,
              })
            }}
          >
            Sugerir
          </Button>
        ) : null}
      </span>
    </div>
  )
}

// Estado da capa vertical aplicada pelo YouTube Studio (card #247). Ícone
// compacto ao lado do título: a coluna de ação não comporta um chip.
const STUDIO_MARK: Record<string, { icon: React.ReactNode; label: string; className: string }> = {
  pending: { icon: <Clock3 />, label: 'Capa na fila do YouTube Studio', className: 'studioPending' },
  applied: { icon: <ImageUp />, label: 'Capa aplicada pelo YouTube Studio', className: 'studioApplied' },
  failed: { icon: <ImageOff />, label: 'A capa não foi aplicada pelo YouTube Studio', className: 'studioFailed' },
  skipped: { icon: <ImageOff />, label: 'Capa sem arquivo no disco', className: 'studioFailed' },
}

function StudioThumbMark({ status, error }: { status: string | null; error: string }) {
  if (!status) return null
  const mark = STUDIO_MARK[status]
  if (!mark) return null
  const text = error ? `${mark.label}: ${error}` : mark.label
  return (
    <Tooltip content={<span style={{ maxWidth: 320, display: 'inline-block' }}>{text}</span>}>
      <span className={cn(styles.studioMark, styles[mark.className])} aria-label={text} role="img">
        {mark.icon}
      </span>
    </Tooltip>
  )
}

function TableSkeleton() {
  return (
    <div className={styles.tableWrap} aria-hidden>
      <div className={cn(styles.row, styles.head)}>
        <span>Veredito</span>
        <span>Vídeo</span>
        <span style={{ textAlign: 'right' }}>Idade</span>
        <span style={{ textAlign: 'right' }}>Retenção</span>
        <span style={{ textAlign: 'right' }}>Views</span>
        <span style={{ textAlign: 'right' }}>Entrega</span>
        <span style={{ textAlign: 'right' }}>Ação</span>
      </div>
      <div className={styles.list}>
        {Array.from({ length: 6 }).map((_, idx) => (
          <div key={`sk-${String(idx)}`} className={styles.row}>
            <div className={cn(styles.skeletonShimmer, styles.skeletonBadge)} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonLine)} style={{ width: '70%' }} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonLine)} style={{ width: '50%', justifySelf: 'end' }} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonLine)} style={{ width: '50%', justifySelf: 'end' }} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonLine)} style={{ width: '40%', justifySelf: 'end' }} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonLine)} style={{ width: '40%', justifySelf: 'end' }} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonLine)} style={{ width: '60%', justifySelf: 'end' }} />
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sort comparators — keyed by SortCol so callers can index without a switch.
// "asc" is the natural reading order: verdict by priority (keep > edit > wait > kill),
// age oldest first, retention worst first, views fewest first, delivery under first.
// The reverse pass in the section handles desc.
// ---------------------------------------------------------------------------

const VERDICT_PRIORITY: Record<RetentionVerdict, number> = {
  keep: 0,
  edit_thumb_title: 1,
  wait: 2,
  kill: 3,
}

const comparators: Record<SortCol, (a: RetentionAuditItem, b: RetentionAuditItem) => number> = {
  verdict: (a, b) => VERDICT_PRIORITY[a.verdict] - VERDICT_PRIORITY[b.verdict],
  age: (a, b) => a.age_days - b.age_days,
  retention: (a, b) => (a.avp ?? -1) - (b.avp ?? -1),
  views: (a, b) => a.views - b.views,
  // delivery: under (below median) first ascending, over after.
  delivery: (a, b) => {
    const order: Record<RetentionAuditItem['delivery_bucket'], number> = { under: 0, over: 1 }
    const diff = order[a.delivery_bucket] - order[b.delivery_bucket]
    if (diff !== 0) return diff
    return a.views - b.views
  },
}

// ---------------------------------------------------------------------------
// Static labels — colocated with the component so a future translation/
// theming pass can swap them without spelunking the service code.
// ---------------------------------------------------------------------------

interface VerdictMeta {
  label: string
  icon: React.ReactNode
}

const VERDICT_META: Record<RetentionVerdict, VerdictMeta> = {
  keep: { label: 'Manter', icon: <ThumbsUp /> },
  edit_thumb_title: { label: 'Editar thumb/título', icon: <Pencil /> },
  wait: { label: 'Aguardar', icon: <Timer /> },
  kill: { label: 'Encerrar', icon: <Skull /> },
}

const VERDICT_TOOLTIP: Record<RetentionVerdict, string> = {
  keep:
    'Vídeos com retenção alta — o teto de views vem de distribuição (canal pequeno), não de embalagem. Não mexer: trocar título ou thumb pode reverter o que o algoritmo já validou.',
  edit_thumb_title:
    'Retenção média/fraca + entrega abaixo da mediana do canal. A embalagem pode estar atraindo o público errado ou não convertendo o swipe; vale testar título e thumb diferentes pra reativar a entrega.',
  wait:
    'Vídeo recém-publicado (menos de 3 dias) ou ainda sem snapshot de retenção. Analytics imaturo — qualquer decisão agora é prematura. O cron diário coleta os dados; volte daqui alguns dias.',
  kill:
    'Retenção fraca em vídeo já maduro (mais de 14 dias). O algoritmo testou e decidiu: a galera não fica até o fim. Não vale gastar tempo otimizando — registre o aprendizado (tema/hook que não funcionou) e siga pro próximo.',
}

const AGE_LABEL: Record<RetentionAuditItem['age_bucket'], string> = {
  fresh: 'fresco',
  proving: 'em prova',
  mature: 'maduro',
}

const RET_LABEL: Record<Exclude<RetentionAuditItem['retention_bucket'], 'unknown'>, string> = {
  strong: 'forte',
  mid: 'média',
  weak: 'fraca',
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}
